/**
 * Work Type(special task)候选 — 对齐 mobile useWorkTypeOptions 语义:
 * user.workTypeCodes 非空 = override;空 = 继承 position;双空 = 全部 special。
 * Redux state.user 只有 userId/fullName/avatarUrl,所以这里现拉 getUser。
 * 仅在 Work Type 视图挂载时使用(组件级懒加载,不常驻)。
 */
import { useEffect, useMemo, useState } from "react";
import { userApi } from "@/api/api";
import { useTaskCategoryData } from "@/components/tasks/TaskCategorySelect/useTaskCategoryData";
import { usePositionWorkTypes } from "@/hooks/usePositionWorkTypes";
import { resolveEffectiveWorkTypes } from "@/components/shared/workTypeInherit";
import type { SnowflakeId } from "@/types/snowflake";

const SPECIAL_ONLY = ["special"];

export interface WorkTypeOption {
  code: string;
  label: string;
  /** 规范英文 label —— 建单标题用它(不随建单人界面语言变),见 utils/subtypeLabel。 */
  canonicalLabel?: string;
  iconUrl?: string | null;
}

export function useWorkTypeOptions(userId: SnowflakeId | null) {
  const { groups, loading: catalogLoading } = useTaskCategoryData(SPECIAL_ONLY);
  const specialSubtypes = useMemo(
    () => groups.find((g) => g.typeCode === "special")?.subtypes ?? [],
    [groups],
  );

  const [stored, setStored] = useState<string[] | null>(null);
  const [positionCode, setPositionCode] = useState<string | null>(null);
  const [userLoading, setUserLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setUserLoading(true);
    userApi
      .getUser(String(userId))
      .then((u: any) => {
        if (cancelled) return;
        // 契约:GET user 应含 workTypeCodes[](空=继承)与 positionTypeCode
        if (u && !Array.isArray(u.workTypeCodes)) {
          console.warn(
            "[useWorkTypeOptions] getUser 缺 workTypeCodes[],response shape:",
            u,
          );
        }
        setStored(Array.isArray(u?.workTypeCodes) ? u.workTypeCodes : []);
        setPositionCode(
          typeof u?.positionTypeCode === "string" ? u.positionTypeCode : null,
        );
      })
      .catch((e) => {
        console.error("[useWorkTypeOptions] getUser failed:", e);
        if (!cancelled) {
          setStored([]);
          setPositionCode(null);
        }
      })
      .finally(() => {
        if (!cancelled) setUserLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const { positionWorkTypes, loading: positionLoading } =
    usePositionWorkTypes(positionCode);

  const options = useMemo<WorkTypeOption[]>(() => {
    const all = specialSubtypes.map((s) => ({
      code: s.code,
      label: s.label,
      canonicalLabel: s.canonicalLabel,
      iconUrl: (s as { iconUrl?: string | null }).iconUrl ?? null,
    }));
    const { codes } = resolveEffectiveWorkTypes(stored, positionWorkTypes);
    if (codes.length === 0) return all; // 双空 → 全部 special(对齐 mobile 回退)
    const allow = new Set(codes);
    const filtered = all.filter((o) => allow.has(o.code));
    return filtered.length > 0 ? filtered : all;
  }, [specialSubtypes, stored, positionWorkTypes]);

  return { options, loading: catalogLoading || userLoading || positionLoading };
}
